import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Wrench, User, Loader2, RotateCcw, Crown } from "lucide-react";
import { getChatHistory, streamChat, getProStatus, getLatestSession, uploadPhoto, photoUrl, LimitReachedError } from "@/lib/api";
import { PhotoButton, PhotoPreview, PhotoUpsell } from "@/components/PhotoAttach";
import { Paywall, UpgradeButton } from "@/components/Paywall";
import { useAuth } from "@/lib/auth";

const MODEL_LABELS = { claude: "Claude", gpt: "ChatGPT" };

const PROMPTS = [
  "What does code P0171 mean?",
  "My brakes grind when I stop - is it safe to drive?",
  "How do I replace brake pads on a 2018 Honda Civic?",
  "Car cranks but won't start, what should I check?",
];

const newSid = () => {
  const s = crypto.randomUUID();
  localStorage.setItem("gh-chat-sid", s);
  return s;
};

const ModelSwitch = ({ model, setModel, disabled }) => (
  <div className="inline-flex border border-[#27303E] rounded-sm p-0.5" data-testid="model-switcher">
    {Object.entries(MODEL_LABELS).map(([k, label]) => (
      <button key={k} disabled={disabled} onClick={() => setModel(k)} data-testid={`model-option-${k}`}
        className={`px-3 h-7 font-mono text-[11px] uppercase tracking-wider rounded-sm transition-colors duration-200 ${model === k ? "bg-amber-500 text-[#0B0D10]" : "text-slate-400 hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

const Bubble = ({ m, i }) => {
  const ai = m.role === "assistant";
  return (
    <div className={`flex gap-3 ${ai ? "" : "flex-row-reverse"}`} data-testid={`chat-message-${i}`}>
      <span className={`hidden sm:grid w-8 h-8 shrink-0 place-items-center rounded-sm ${ai ? "bg-amber-500 text-[#0B0D10]" : "bg-[#27303E] text-slate-200"}`}>
        {ai ? <Wrench size={16} /> : <User size={16} />}
      </span>
      <div className={`max-w-[88%] sm:max-w-[85%] min-w-0 break-words px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-sm text-[15px] leading-relaxed ${ai ? "bg-[#13171F] border border-[#27303E] prose-chat" : "bg-amber-500/15 border border-amber-500/30"}`}>
        {ai && m.model && <p className="!mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid={`chat-message-model-${i}`}>{MODEL_LABELS[m.model]}</p>}
        {!ai && (m.localUrl || m.image_id) && (
          <img src={m.localUrl || photoUrl(m.image_id)} alt="Your photo" className="mb-2 w-full max-w-[260px] rounded-sm border border-amber-500/30" data-testid={`chat-message-photo-${i}`} />
        )}
        {ai ? <ReactMarkdown>{m.content || "..."}</ReactMarkdown> : m.content}
      </div>
    </div>
  );
};

export const ChatPanel = ({ initialPrompt }) => {
  const [sid, setSid] = useState(() => localStorage.getItem("gh-chat-sid") || newSid());
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState(initialPrompt || "");
  const [busy, setBusy] = useState(false);
  const [model, setModelState] = useState(() => localStorage.getItem("gh-model") || "claude");
  const [photo, setPhoto] = useState(null);
  const [upsell, setUpsell] = useState(false);
  const endRef = useRef(null);
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: pro } = useQuery({ queryKey: ["pro"], queryFn: getProStatus });
  const left = pro ? Math.max(pro.limit - pro.used, 0) : null;
  const locked = pro && !pro.is_pro && left === 0;

  const setModel = (m) => { localStorage.setItem("gh-model", m); setModelState(m); };

  useEffect(() => {
    if (!user) return;
    getLatestSession().then((r) => {
      if (r.session_id) { localStorage.setItem("gh-chat-sid", r.session_id); setSid(r.session_id); }
    }).catch(() => {});
  }, [user]);
  useEffect(() => { getChatHistory(sid).then(setMsgs).catch(() => {}); }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (text) => {
    const t = (text ?? input).trim() || (photo ? "Please diagnose what's in this photo." : "");
    if (!t || busy || locked) return;
    const pic = photo;
    setInput("");
    setPhoto(null);
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: t, localUrl: pic?.url }, { role: "assistant", content: "", model }]);
    const append = (d) => setMsgs((m) => {
      const c = [...m];
      c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d };
      return c;
    });
    try {
      const imageId = pic ? (await uploadPhoto(pic.blob)).image_id : null;
      await streamChat(sid, t, append, model, imageId);
    } catch (e) {
      if (e instanceof LimitReachedError) setMsgs((m) => m.slice(0, -2));
      else append("\n\n_Sorry, the AI mechanic is unavailable right now. Please try again._");
    }
    qc.invalidateQueries({ queryKey: ["pro"] });
    setBusy(false);
  };

  return (
    <div className="flex flex-col min-w-0 h-[calc(100dvh-13.5rem)] min-h-[420px] sm:h-[70vh] sm:min-h-[520px] bg-[#0F1218] border border-[#27303E] rounded-sm" data-testid="chat-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2.5 sm:py-3 border-b border-[#27303E]">
        <ModelSwitch model={model} setModel={setModel} disabled={busy} />
        <div className="flex items-center gap-3 sm:gap-4">
          {pro?.is_pro ? (
            <span className="inline-flex items-center gap-1 font-mono text-xs uppercase text-amber-400" data-testid="pro-badge"><Crown size={13} /> Pro - unlimited</span>
          ) : pro && (
            <span className="font-mono text-xs uppercase text-slate-400" data-testid="free-messages-left">{left} / {pro.limit} free left</span>
          )}
          <button onClick={() => { setMsgs([]); setSid(newSid()); }} data-testid="chat-new-session-btn" className="inline-flex items-center gap-1 font-mono text-xs uppercase text-slate-400 hover:text-amber-500 transition-colors duration-200">
            <RotateCcw size={13} /> New chat
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5 space-y-4 sm:space-y-5" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="min-h-full flex flex-col justify-center gap-3 sm:gap-4">
            <p className="font-display text-2xl sm:text-3xl font-bold uppercase">What's your car doing?</p>
            <p className="text-slate-400 text-sm">Describe the symptom, sound, smell, or code. Include year, make and model for the best answer.</p>
            <div className="grid sm:grid-cols-2 gap-2 sm:gap-3 mt-1 sm:mt-2">
              {PROMPTS.map((p, i) => (
                <button key={p} onClick={() => send(p)} data-testid={`chat-prompt-${i}`} className="text-left text-sm p-3 sm:p-4 bg-[#13171F] active:border-amber-500 border border-[#27303E] hover:border-amber-500 rounded-sm transition-colors duration-200">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {locked && <Paywall />}
        <div ref={endRef} />
      </div>
      {pro && !pro.is_pro && left > 0 && left <= 2 && (
        <div className="px-4 py-2 border-t border-[#27303E] flex items-center justify-between gap-3 text-sm text-slate-400" data-testid="low-messages-banner">
          Only {left} free message{left > 1 ? "s" : ""} left.
          <UpgradeButton className="h-9 px-4 text-sm" label="Go Pro $9.99" />
        </div>
      )}
      {upsell && !pro?.is_pro && <PhotoUpsell onClose={() => setUpsell(false)} />}
      {photo && <PhotoPreview photo={photo} onRemove={() => setPhoto(null)} onPrompt={(p) => send(p)} />}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className={`p-2.5 sm:p-4 flex gap-2 sm:gap-3 ${photo ? "" : "border-t border-[#27303E]"}`}>
        <PhotoButton isPro={!!pro?.is_pro} disabled={busy || locked} onPick={setPhoto} onUpsell={() => setUpsell(true)} />
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={locked}
          placeholder={locked ? "Upgrade to Pro to keep chatting" : photo ? "Ask about this photo..." : "Describe the symptom..."}
          enterKeyHint="send"
          autoComplete="off"
          data-testid="chat-input"
          className="flex-1 min-w-0 h-12 px-3 sm:px-4 bg-[#13171F] border border-[#27303E] focus:border-amber-500 disabled:opacity-50 outline-none rounded-sm transition-colors duration-200"
        />
        <button type="submit" disabled={busy || locked} data-testid="chat-send-btn" aria-label="Send" className="h-12 w-12 sm:w-auto sm:px-5 shrink-0 inline-flex items-center justify-center gap-2 active:scale-95 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0B0D10] font-display font-bold uppercase rounded-sm transition-colors duration-200">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} <span className="hidden sm:inline">Send</span>
        </button>
      </form>
    </div>
  );
};
